import i18n from '@gripp/i18n';
import { useGrippContext } from '@gripp/shared-logic';
import { Colors } from '@gripp/shared-ui';
import {
  FallbackProps,
  ErrorBoundary as ReactErrorBoundary,
} from 'react-error-boundary';
import { Image, StyleSheet, Text, View } from 'react-native';
import { Button } from 'react-native-paper';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  inline?: boolean;
}

export function ErrorBoundary({
  children,
  inline = false,
}: ErrorBoundaryProps) {
  const { logger } = useGrippContext();
  return (
    <ReactErrorBoundary
      FallbackComponent={(props) => (
        <ErrorFallback {...props} inline={inline} />
      )}
      onError={(e, i) => logger?.fatal(e, i?.componentStack as string)}
    >
      {children}
    </ReactErrorBoundary>
  );
}

interface ErrorFallbackProps extends FallbackProps {
  inline?: boolean;
}

const ErrorFallback = ({
  resetErrorBoundary,
  inline = false,
}: ErrorFallbackProps) => {
  return (
    <View style={[styles.container, inline && styles.inlineContainer]}>
      <View style={styles.errorBox}>
        <Image
          style={styles.image}
          source={{ uri: '/oops.png' }}
          resizeMode="contain"
        />
        <Text style={styles.title}>{i18n.t('errorBoundary.title')}</Text>
        <Text style={styles.message}>{i18n.t('errorBoundary.message')}</Text>
        <Button
          mode="contained"
          buttonColor={Colors.primary}
          onPress={resetErrorBoundary}
          style={styles.button}
          labelStyle={styles.buttonLabel}
        >
          {i18n.t('errorBoundary.tryAgain')}
        </Button>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: Colors.role.canvas,
  },
  inlineContainer: {
    backgroundColor: 'transparent',
  },
  errorBox: {
    maxWidth: 400,
    padding: 40,
    backgroundColor: Colors.role.surface,
    borderRadius: 12,
    alignItems: 'center',
  },
  image: {
    width: 380,
    height: 431,
    marginBottom: 20,
  },
  title: {
    fontSize: 36,
    fontWeight: '700',
    color: Colors.primary,
    marginBottom: 8,
    textAlign: 'center',
  },
  message: {
    fontSize: 18,
    lineHeight: 26,
    textAlign: 'center',
    marginBottom: 20,
    color: Colors.role.textStrong,
  },
  button: {
    marginTop: 8,
  },
  buttonLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.role.textStrong,
  },
});
